// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import IconStepperMinus from "@stoneage/XDL/Icons/IconStepperMinus";
import IconStepperPlus from "@stoneage/XDL/Icons/IconStepperPlus";
import { mergeWithCss } from "@stoneage/XDL/Utilities";
import React, { useEffect, useState } from "react";

interface OwnProps extends Omit<TextFieldProps, "css"> {
    onDecreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    onIncreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    showActionButton?: boolean;
    showNoPriceAlert?: boolean;
    extendedStyles?: QtyFieldStyles;
    allowZero?: boolean;
    editable?: boolean;
    showLabel?: boolean;
    min?: number | string;
    max?: number | string;
    validationRules?: string;
}

type Props = OwnProps;

export interface QtyFieldStyles {
    container?: InjectableCss;
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    qtyLabel?: TypographyProps;
    valueText?: TypographyProps;
    actionButton?: ClickablePresentationProps;
    qtyLabelNotEditable?: TypographyProps;
}

export const qtyFieldStyles: QtyFieldStyles = {};

const enterKey = 13;

const QtyField = ({
    extendedStyles,
    onIncreaseChangeHandler,
    onDecreaseChangeHandler,
    disabled: disabledProps = false,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    label: labelOverride,
    showActionButton = true,
    showNoPriceAlert = false,
    allowZero = false,
    editable = true,
    showLabel = false,
    min = 0,
    max = 9999,
    validationRules,
    ...qtyFieldsProps
}: Props) => {
    const [styles] = useState(() => mergeWithCss(qtyFieldStyles, extendedStyles));
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);
    const [qtyError, setQtyError] = useState("");

    const minQty = Number(min);
    const maxQty = Number(max);

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChangeTextField?.(event);
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        // if (event?.currentTarget) {
        //     onChangeHandler(event);
        // }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && isNumeric(`${currentValue}`) && Number(currentValue) - 1 >= 0
                ? Number(currentValue) - 1
                : undefined;

        if (allowZero && nextValue === undefined) {
            return;
            // eslint-disable-next-line no-else-return
        } else if (!allowZero && !nextValue) {
            return;
        }

        event.currentTarget.value = nextValue;
        event.target.value = nextValue;

        if (onDecreaseChangeHandler) {
            event = onDecreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const onIncreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && isNumeric(`${currentValue}`) && Number(currentValue) + 1 <= maxQty
                ? Number(currentValue) + 1
                : undefined;

        if (!nextValue) {
            return;
        }

        event.currentTarget.value = nextValue;
        event.target.value = nextValue;

        if (onIncreaseChangeHandler) {
            event = onIncreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const qtyTextField = (
        <TextField
            type="qty"
            disabled={disabled}
            value={currentValue}
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            onClick={onClickHandler}
            onKeyDown={onKeyDownHandler}
            {...styles.editableQuantityTextField}
            {...qtyFieldsProps}
            aria-label={translate("QTY")}
            {...{ showActionButton }}
        />
    );
    if (editable) {
        return (
            <StyledWrapper {...styles.container} data-test-selector="qty-field">
                {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                <VisuallyHidden as="label">{translate("QTY")}</VisuallyHidden>
                <StyledWrapper {...styles.wrapper} {...{ showActionButton, isDisabled: disabled }}>
                    {showActionButton && (
                        <Clickable
                            {...styles.actionButton}
                            disabled={disabled || Number(currentValue) <= minQty}
                            onClick={onDecreaseClickHandler}
                            data-test-selector="product_qtyOrdered_decrease"
                            aria-label="decrease quantity"
                        >
                            <Icon src={IconStepperMinus} />
                        </Clickable>
                    )}
                    {qtyTextField}
                    {showActionButton && (
                        <Clickable
                            {...styles.actionButton}
                            disabled={disabled || Number(currentValue) >= maxQty}
                            onClick={onIncreaseClickHandler}
                            data-test-selector="product_qtyOrdered_increase"
                            aria-label="increase quantity"
                        >
                            <Icon src={IconStepperPlus} />
                        </Clickable>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
    return (
        <>
            <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
            {showLabel && <Typography {...styles.qtyLabelNotEditable}>{labelOverride || translate("QTY")}</Typography>}
            <Typography {...styles.valueText}>{currentValue}</Typography>
        </>
    );
};

export default QtyField;
